import { useEffect } from 'react';
import { useNavigate, Outlet, useMatch } from 'react-router-dom';
import { Tabs, PermissionDenied, PermissionEnum, IconLoader } from '@ui/src';
import { useAppSelector } from '@store';
import ROUTES from '../../routes';
import {
  useLazyGetEmployeesBaseDictionariesQuery,
  useLazyGetActiveCurrenciesQuery,
  useLazyGetAdministrationCountriesQuery,
} from '../../store/dictionaries/service';
import { useLazyGetPSPDetailsValidatorsQuery } from '../settings/store/services/details';

import cn from '../settings/styles.module.css';

const TransactionSettingsPage = (): React.JSX.Element => {
  const navigate = useNavigate();

  const match = useMatch(ROUTES.TRANSACTION_SETTINGS);

  const {
    administrationDictionaries: {
      activePermissions,
      roles,
      activeCurrencies,
      countries,
    },
    settings: {
      details: { pspDetails },
    },
  } = useAppSelector((state) => state);

  const [getEmployeesBaseDictionaries] =
    useLazyGetEmployeesBaseDictionariesQuery();
  const [getActiveCurrencies] = useLazyGetActiveCurrenciesQuery();
  const [getEmployeesCountries] = useLazyGetAdministrationCountriesQuery();
  const [getPspDetails] = useLazyGetPSPDetailsValidatorsQuery();

  useEffect(() => {
    if (
      match &&
      activePermissions?.includes(PermissionEnum.SETTINGS_SYSTEM_LIST)
    ) {
      navigate(ROUTES.TRANSACTION_SETTINGS_TR_TYPES);

      if (!roles) {
        getEmployeesBaseDictionaries();
      }

      if (!countries) {
        getEmployeesCountries();
      }

      if (!activeCurrencies) {
        getActiveCurrencies();
      }

      if (!pspDetails) {
        getPspDetails();
      }
    }
  }, [match, activePermissions, roles, countries, activeCurrencies]);

  useEffect(() => {
    let title = 'Transaction Settings';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content = (
    <div className={cn.settingsLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions) {
    if (
      !activePermissions.includes(PermissionEnum.SECTION_ADMINISTRATION) ||
      !activePermissions.includes(PermissionEnum.SETTINGS_SYSTEM_LIST)
    ) {
      content = <PermissionDenied />;
    } else {
      content = (
        <div className={cn.settings} id="settings">
          <div className={cn.settingsTitle}>Transaction Settings</div>
          <div className={cn.settingsTabWrapper}>
            <Tabs
              itemClassName={cn.settingsTabItem}
              tabs={[
                {
                  name: 'Transaction types',
                  path: ROUTES.TRANSACTION_SETTINGS_TR_TYPES,
                },
                {
                  name: 'Payment types',
                  path: ROUTES.TRANSACTION_SETTINGS_PAYMENT_TYPES,
                },
                {
                  name: 'Beneficiaries',
                  path: ROUTES.TRANSACTION_SETTINGS_BENEFICIARIES,
                },
                {
                  name: 'Transactions options',
                  path: ROUTES.TRANSACTION_SETTINGS_OPTIONS,
                },
                {
                  name: 'CASPs',
                  path: ROUTES.TRANSACTION_SETTINGS_CASP,
                  hidden: !activePermissions.includes(PermissionEnum.CASP_LIST),
                },
                {
                  name: 'Overdraft',
                  path: ROUTES.TRANSACTION_SETTINGS_OVERDRAFT,
                },
                {
                  name: 'Processing Rules',
                  path: ROUTES.TRANSACTION_SETTINGS_PROCESSING_RULES,
                  hidden: !activePermissions?.includes(
                    PermissionEnum.TRANSACTIONS_RULES_LIST,
                  ),
                },
                {
                  name: 'IBAN/SWIFT validation',
                  path: ROUTES.TRANSACTION_SETTINGS_IBAN,
                },
                {
                  name: 'Purpose codes',
                  path: ROUTES.TRANSACTION_SETTINGS_PURPOSE_CODE,
                  hidden: !activePermissions.includes(
                    PermissionEnum.PURPOSE_CODE_SETTINGS_LIST,
                  ),
                },
              ]}
              enableRouting
            />
          </div>

          <Outlet />
        </div>
      );
    }
  }

  return content;
};

export default TransactionSettingsPage;
